import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Linkedin, Instagram, FileText, ArrowUpRight, Loader2 } from "lucide-react";
import { Chapter } from "./Reveal";
import { PROFILE } from "../../data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Connect = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — I'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { icon: Linkedin, label: "LinkedIn", href: PROFILE.linkedin },
    { icon: Instagram, label: "Instagram", href: PROFILE.instagram },
    { icon: FileText, label: "Résumé", href: PROFILE.resume },
    { icon: Mail, label: "Email", href: `mailto:${PROFILE.email}` },
  ];

  return (
    <section
      id="connect"
      className="relative overflow-hidden py-24 md:py-36"
      data-testid="connect-section"
    >
      <div
        aria-hidden
        className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full blur-[150px]"
        style={{ background: "radial-gradient(circle, rgba(94,126,155,0.14), transparent 70%)" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <Chapter num="04" label="Initiate" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* left: big CTA */}
          <div className="lg:col-span-6">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter leading-[0.9]"
            >
              Let's build
              <br />
              something
              <br />
              <span className="text-[#5E7E9B] text-glow">memorable.</span>
            </motion.h2>

            <a
              href={`mailto:${PROFILE.email}`}
              className="group inline-flex items-center gap-3 mt-10 font-display text-2xl md:text-3xl text-[#404B5A] hover:text-[#1F2D3D] transition-colors break-all"
              data-testid="connect-email"
            >
              <span className="border-b border-[#1F2D3D]/15 group-hover:border-[#5E7E9B] pb-1 transition-colors">
                {PROFILE.email}
              </span>
              <ArrowUpRight className="shrink-0 group-hover:text-[#5E7E9B] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>

            <div className="mt-12 grid grid-cols-2 gap-px bg-[#1F2D3D]/[0.04] border border-[#1F2D3D]/10 rounded-lg overflow-hidden max-w-md">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center justify-between bg-white p-5 hover:bg-[#1F2D3D]/[0.03] transition-colors"
                  data-testid={`social-${s.label.toLowerCase()}`}
                >
                  <span className="flex items-center gap-3 text-[#404B5A] group-hover:text-[#1F2D3D] transition-colors">
                    <s.icon size={18} className="text-[#5E7E9B]" />
                    {s.label}
                  </span>
                  <ArrowUpRight size={14} className="text-[#9AA3AF] group-hover:text-[#5E7E9B] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </a>
              ))}
            </div>
          </div>

          {/* right: form */}
          <div className="lg:col-span-6">
            <form
              onSubmit={submit}
              className="glass glow-cyan border border-[#1F2D3D]/10 rounded-2xl p-7 md:p-10"
              data-testid="contact-form"
            >
              <div className="space-y-6">
                <FormField label="Name" testId="contact-name">
                  <input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="input-line"
                    data-testid="contact-name-input"
                  />
                </FormField>
                <FormField label="Email" testId="contact-email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="input-line"
                    data-testid="contact-email-input"
                  />
                </FormField>
                <FormField label="Subject" testId="contact-subject">
                  <input
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Internship / collaboration"
                    className="input-line"
                    data-testid="contact-subject-input"
                  />
                </FormField>
                <FormField label="Message" testId="contact-message">
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell me a little about the role or project..."
                    className="input-line resize-none"
                    data-testid="contact-message-input"
                  />
                </FormField>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-[#5E7E9B] text-white font-mono text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#1F2D3D] disabled:opacity-60 transition-colors duration-300"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-24 pt-8 border-t border-[#1F2D3D]/10 flex flex-col md:flex-row justify-between gap-4 font-mono text-xs text-[#9AA3AF]">
          <span>© {new Date().getFullYear()} {PROFILE.name} — {PROFILE.location}</span>
          <span>Designed & built with precision.</span>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A8492] mb-2">
      {label}
    </label>
    {children}
  </div>
);
